/**
 * WooCommerce product & cart detection (platform layer)
 *
 * Everything in this file reads WooCommerce-rendered DOM: the .pmwProductId
 * markers printed for every product in a WooCommerce loop, the cart item
 * rows, WooCommerce's session/cart cookies, and the show_variation jQuery
 * event WooCommerce core triggers on variable products. Extracted from
 * pmw/functions.js in phase 4 of docs/PLATFORM-ABSTRACTION-PLAN.md; the
 * function bodies are unchanged.
 *
 * jQuery is fine here: this file only ever runs on WordPress.
 *
 * @since 1.65.0
 */

(function (pmw, $, undefined) {

	pmw.triggerViewItemList = (productId, marker = null) => {

		if (!productId) throw Error("Wasn't able to retrieve a productId");

		productId = pmw.getIdBasedOndVariationsOutputSetting(productId);

		if (!productId) throw Error("Wasn't able to retrieve a productId");

		const product = pmw.addListContextToProduct(pmw.getProductDataForViewItemEvent(productId), marker);

		if (product) {
			pmw.console.log(`view_item_list detected: product ${product.id} in list "${product.list_name}", position ${product.list_position}`);
		}

		pmw.bus.emit("pmw:view-item-list", product);
	};

	/**
	 * Product list containers, from the most specific to the most generic.
	 * WooCommerce core and most themes (WoodMart, Flatsome, Astra) wrap a
	 * loop in .products, the block grids use their own classes.
	 */
	const LIST_CONTAINER_SELECTOR = [
		".wc-block-grid__products",
		".wc-block-product-template",
		".wp-block-woocommerce-product-template",
		".products",
	].join(",");

	/**
	 * Headings that title a product list in WooCommerce core and common
	 * themes and page builders. Matched outside of the product tiles only.
	 */
	const LIST_TITLE_SELECTOR = "h1,h2,h3,h4,.wd-el-title,.widget-title,.section-title,.title";

	const PAGE_LEVEL_LIST_PAGE_TYPES = ["product_shop", "product_category", "product_tag", "search"];

	const LOOP_NAME_LABELS = {
		"related"              : "Related Products",
		"up-sells"             : "Upsells",
		"cross-sells"          : "Cross-Sells",
		"products"             : "Products",
		"recent_products"      : "Recent Products",
		"sale_products"        : "Sale Products",
		"best_selling_products": "Best Selling Products",
		"top_rated_products"   : "Top Rated Products",
		"featured_products"    : "Featured Products",
		"product_category"     : "Product Category",
		"product_cat"          : "Product Category",
	};

	// Lists already named on this page, so a list keeps its ID when more
	// products load into it or new lists appear further down. A key is taken
	// only while its list is still in the document: a list that a block
	// re-renders (pagination, filters) gets its old key back.
	const listContexts = new WeakMap();
	const listKeys     = new Map();

	const slugify = text => String(text)
		.toLowerCase()
		.normalize("NFKD")
		.replace(/[\u0300-\u036f]/g, "")
		.replace(/[^a-z0-9]+/g, "_")
		.replace(/^_+|_+$/g, "")
		.slice(0, 50);

	const getMarkersOf = container => Array.from(container.querySelectorAll(".pmwProductId"))
		.filter(marker => marker.closest(LIST_CONTAINER_SELECTOR) === container);

	/**
	 * Find the title of a product list: the nearest heading in the list's
	 * own wrappers that is not part of a product tile and whose wrapper holds
	 * no other product list.
	 */
	const findListTitle = container => {

		let wrapper = container.parentElement;

		for (let level = 0; level < 4 && wrapper && wrapper !== document.body; level++, wrapper = wrapper.parentElement) {

			const otherLists = Array.from(wrapper.querySelectorAll(LIST_CONTAINER_SELECTOR))
				.filter(list => list !== container && !container.contains(list) && !list.contains(container) && getMarkersOf(list).length);

			if (otherLists.length) return null;

			const title = Array.from(wrapper.querySelectorAll(LIST_TITLE_SELECTOR))
				.find(heading => !container.contains(heading) && !heading.closest(".product, .pmwProductId") && heading.textContent.trim());

			if (title) return title.textContent.trim().replace(/\s+/g, " ").slice(0, 100);
		}

		return null;
	};

	const isMainList = (container, marker) => {

		if (marker?.dataset?.list === "main") return true;

		if (!PAGE_LEVEL_LIST_PAGE_TYPES.includes(pmwDataLayer?.shop?.page_type)) return false;

		// No marker on the page flags the main loop (block themes, cached HTML
		// from before 1.71.1): the list with the most products is the main one
		if (document.querySelector(".pmwProductId[data-list='main']")) return false;

		const largest = getListContainersOnPage().reduce((best, list) => !best || getMarkersOf(list).length > getMarkersOf(best).length ? list : best, null);

		return largest === container;
	};

	const isListKeyTaken = (key, container) => {
		const owner = listKeys.get(key);
		return Boolean(owner && owner !== container && owner.isConnected);
	};

	const getListContainersOnPage = () => Array.from(new Set(Array.from(document.querySelectorAll(".pmwProductId"))
		.map(element => element.closest(LIST_CONTAINER_SELECTOR))
		.filter(Boolean)));

	/**
	 * Name the lists in document order, so the second of two lists with the
	 * same title is always the one with the suffix, whichever the visitor
	 * scrolled to first.
	 */
	const getListContextOfContainer = (container, marker) => {

		if (listContexts.has(container)) return listContexts.get(container);

		getListContainersOnPage()
			.filter(list => list !== container && !listContexts.has(list) && list.compareDocumentPosition(container) & Node.DOCUMENT_POSITION_FOLLOWING)
			.forEach(list => createListContext(list, list.querySelector(".pmwProductId")));

		return createListContext(container, marker);
	};

	const createListContext = (container, marker) => {

		const pageListId   = pmwDataLayer?.shop?.list_id;
		const pageListName = pmwDataLayer?.shop?.list_name;

		let context;

		if (isMainList(container, marker)) {
			context = {list_id: pageListId, list_name: pageListName};
		} else {
			const loopName = marker?.dataset?.list && marker.dataset.list !== "main" ? marker.dataset.list : null;
			const title    = findListTitle(container);

			const key = (loopName ? slugify(loopName) : (title ? slugify(title) : "")) || "list";

			// Two lists with the same name on one page stay apart
			let uniqueKey = key;
			for (let i = 2; isListKeyTaken(uniqueKey, container); i++) uniqueKey = `${key}_${i}`;
			listKeys.set(uniqueKey, container);

			const label = title || LOOP_NAME_LABELS[loopName] || uniqueKey;

			context = {
				list_id  : `${pageListId}.${uniqueKey}`,
				list_name: `${pageListName} | ${label}`,
			};
		}

		context = pmw.hooks?.applyFilters ? pmw.hooks.applyFilters("pmw_product_list_context", context, container) : context;

		listContexts.set(container, context);

		return context;
	};

	/**
	 * The product list a product marker belongs to, with the product's
	 * 1-based position in that list.
	 *
	 * Lists are the product containers on the page: the main loop of a
	 * shop, category, tag or search page keeps the page-level list ID and
	 * name, every other list (related products, theme and page builder
	 * widgets) gets its own, derived from the page list and the list's loop
	 * name or title. The pmw_product_list_context JS filter can rename them.
	 *
	 * @param {Element|jQuery|null} marker The .pmwProductId element of the product tile
	 * @returns {{list_id: string, list_name: string, list_position: number}|null}
	 *
	 * @since 1.71.1
	 */
	pmw.getListContextForMarker = marker => {

		try {
			const element = marker && marker.jquery ? marker[0] : marker;
			if (!element || !element.closest) return null;

			const container = element.closest(LIST_CONTAINER_SELECTOR);
			if (!container) return null;

			const context  = getListContextOfContainer(container, element);
			const position = getMarkersOf(container).indexOf(element) + 1;

			return {
				list_id      : context.list_id,
				list_name    : context.list_name,
				list_position: position > 0 ? position : undefined,
			};
		} catch (e) {
			console.error(e);
			return null;
		}
	};

	/**
	 * Add the list context of a product tile to an event product.
	 *
	 * Without a marker or a list container the product keeps the page-level
	 * list name and the data layer position.
	 *
	 * @param {object|null|undefined} product
	 * @param {Element|jQuery|null}   marker
	 * @returns {object|null|undefined}
	 *
	 * @since 1.71.1
	 */
	pmw.addListContextToProduct = (product, marker) => {

		if (!product) return product;

		const context = pmw.getListContextForMarker(marker);

		if (!context) return product;

		product.list_id   = context.list_id;
		product.list_name = context.list_name;
		if (context.list_position) product.list_position = context.list_position;

		return product;
	};

	pmw.getMainProductIdFromProductPage = () => {

		try {
			if (["simple", "variable", "grouped", "composite", "bundle"].indexOf(pmwDataLayer.shop.product_type) >= 0) {
				return jQuery(".pmwProductId:first").data("id");
			} else {
				return false;
			}
		} catch (e) {
			console.error(e);
		}
	};

	/**
	 * Collect the product IDs of the products rendered in a product listing
	 *
	 * Reads the .pmwProductId markers that the Pixel Manager prints for every
	 * product of a WooCommerce loop, in DOM order, so the result mirrors the order
	 * the visitor sees. Duplicates are dropped, and only IDs that resolve in
	 * pmwDataLayer.products are returned, which keeps out markers whose product
	 * data never made it into the data layer.
	 *
	 * @since 1.64.1
	 *
	 * @returns {string[]} Product IDs in listing order
	 */
	pmw.getProductIdsFromListing = () => {

		try {
			return jQuery(".pmwProductId")
				.map((index, element) => String(jQuery(element).data("id")))
				.get()
				.filter((productId, index, productIds) =>
					productIds.indexOf(productId) === index && pmwDataLayer?.products?.[productId],
				);
		} catch (e) {
			console.error(e);
			return [];
		}
	};

	pmw.viewItemListTriggerTestMode = target => {

		jQuery(target).css({"position": "relative"});
		jQuery(target).append("<div id=\"viewItemListTriggerOverlay\"></div>");
		jQuery(target).find("#viewItemListTriggerOverlay").css({
			"z-index"         : "10",
			"display"         : "block",
			"position"        : "absolute",
			"height"          : "100%",
			"top"             : "0",
			"left"            : "0",
			"right"           : "0",
			"opacity"         : pmwDataLayer.shop.view_item_list_trigger.opacity,
			"background-color": pmwDataLayer.shop.view_item_list_trigger.background_color,
		});
	};

	// we need this to track timeouts for intersection observers
	let ioTimeouts = {};

	pmw.observerCallback = (entries, observer) => {

		entries.forEach((entry) => {

			try {
				let elementId = jQuery(entry.target).data("ioid");

				// Get the productId from next element, if wpmProductId is a sibling, like in Gutenberg blocks
				// otherwise go search in children, like in regular WC loop items
				const marker = jQuery(entry.target).next(".pmwProductId").length
					? jQuery(entry.target).next(".pmwProductId").first()
					: jQuery(entry.target).find(".pmwProductId").first();

				const productId = marker.data("id");


				if (!productId) throw Error("pmwProductId element not found");

				if (entry.isIntersecting) {

					ioTimeouts[elementId] = setTimeout(() => {

						pmw.triggerViewItemList(productId, marker);
						if (pmwDataLayer.shop.view_item_list_trigger.test_mode) pmw.viewItemListTriggerTestMode(entry.target);
						if (pmwDataLayer.shop.view_item_list_trigger.repeat === false) observer.unobserve(entry.target);
					}, pmwDataLayer.shop.view_item_list_trigger.timeout);

				} else {

					clearTimeout(ioTimeouts[elementId]);
					if (pmwDataLayer.shop.view_item_list_trigger.test_mode) jQuery(entry.target).find("#viewItemListTriggerOverlay").remove();
				}
			} catch (e) {
				console.error(e);
			}
		});
	};

	// fire view_item_list only on products that have become visible
	let io;
	let ioid = 0;

	const getCommonIdentifiersToWatch = () => {

		let listOfClassNames = {
			nested: [],
			flat  : [],
		};

		try {

			const haveSameNestingDepth = (element1, element2) => {

				// If both are the same, return the parent
				return jQuery(element1).parents().length === jQuery(element2).parents().length;
			};

			const findCommonParentElement = (element1, element2) => {
				// If both are the same, return the parent
				if (jQuery(element1).parent().is(jQuery(element2).parent())) {
					return {
						node: jQuery(element1).parent(),
						type: "flat",
					};
				}

				return {
					node: jQuery(element1).parents().has(jQuery(element2).parents()).first(),
					type: "nested",
				};
			};

			const countOfChildren = (element) => {
				// Return the count of "wpmProductId" elements
				return jQuery(element).find(".pmwProductId").length;
			};

			const getCommonClass = (classes1, classes2) => {

				// console.log("classes1", classes1);
				// console.log("classes2", classes2);
				// classes 1 and 2 are arrays of classes
				// They may contain the same classes, but not in the same order
				// Return the first common class that's present in both arrays

				return classes1.filter(value => classes2.includes(value))[0] || null;
			};

			const pmwProductIds = document.querySelectorAll(".pmwProductId");

			// If zero or only one product template is found, then we can't run the algorithm
			if (pmwProductIds.length === 1) {
				// console.log("No product or too few templates found")
				return listOfClassNames;
			}

			// console.log("wpmProductIds", wpmProductIds);
			// console.log("looping")

			for (let i = 0; i < pmwProductIds.length - 1; i++) {

				// console.log("i", i)

				if (!haveSameNestingDepth(pmwProductIds[i], pmwProductIds[i + 1])) {
					// console.log("The nesting depth is not the same")
					i++;
					continue;
				}

				let productsNode = findCommonParentElement(pmwProductIds[i], pmwProductIds[i + 1]);
				// console.log("productsNode.node", productsNode.node)
				// console.log("productsNode.type", productsNode.type)

				// If the productsNode is nested, get the first class of the first child. Then console log the class
				if (productsNode.type === "nested") {

					let firstChildClassAttr = productsNode.node.children().first().attr("class");
					let firstChildClasses   = firstChildClassAttr ? firstChildClassAttr.split(" ") : [];

					let secondChildClassAttr = productsNode.node.children().first().next().attr("class");
					let secondChildClasses   = secondChildClassAttr ? secondChildClassAttr.split(" ") : [];

					const commonClass = getCommonClass(firstChildClasses, secondChildClasses);

					listOfClassNames.nested.push(commonClass);
				} else if (productsNode.type === "flat") {

					// If class is not found, continue
					if (!productsNode.node.children().first().attr("class")) continue;

					// firstProductTemplateClasses is the classes of the first child element of the productsNode
					let firstProductTemplateClasses = productsNode.node.children().first().attr("class").split(" ")[0];

					listOfClassNames.flat.push(firstProductTemplateClasses);
				}

				// which wpmProductIds is the last one in the productsNode? console log the index of the last wpmProductId in the productsNode
				// console.log("productsNode.children().length", countOfChildren(productsNode));

				i = i + countOfChildren(productsNode.node) - 1;
			}

			// Make the list of class names unique
			listOfClassNames.nested = [...new Set(listOfClassNames.nested)];
			listOfClassNames.flat   = [...new Set(listOfClassNames.flat)];

			return listOfClassNames;

		} catch (e) {
			console.error(e);
			return listOfClassNames;
		}
	};


	let detectedProductTemplateClasses = {
		nested: [],
		flat  : [],
	};

	const isNestedClass = (elem) => {
		let nestedClassNames = [
			"product",
			"type-product",
			"product-item-inner",
			...detectedProductTemplateClasses.nested,
		];

		return nestedClassNames.some(className => jQuery(elem).closest("." + className).length);
	};

	const isFlatClass = (elem) => {

		let flatClassNames = [
			"wc-block-grid__product",
			"product",
			"product-small",
			"woocommerce-LoopProduct-link",
			...detectedProductTemplateClasses.flat,
		];

		return flatClassNames.some(className => jQuery(elem).prev().hasClass(className));
	};

	const getAllElementsToWatch = () => {

		detectedProductTemplateClasses = getCommonIdentifiersToWatch();

		return jQuery(".pmwProductId")
			.map(function (i, elem) {

				if (isNestedClass(elem)) {
					return jQuery(elem).parent();
				} else if (isFlatClass(elem)) {
					return jQuery(this).prev();
				} else if (jQuery(elem).closest(".product").length) {
					return jQuery(elem).closest(".product");
				}
			});
	};

	/**
	 * Hand every product element that is not watched yet to the intersection
	 * observer.
	 *
	 * Idempotent, because it runs again after every client-side re-render:
	 * blocks that run on the Interactivity API replace whole regions of the
	 * page on pagination and filtering, and the elements that survive such a
	 * pass must not be observed a second time.
	 */
	const observeProductElements = () => {

		if (!io) return;

		getAllElementsToWatch().each((i, elem) => {

			const node = elem[0] ? elem[0] : elem;

			if (!node) return;
			if (jQuery(node).data("pmwObserved")) return;

			jQuery(node).data("pmwObserved", true);
			jQuery(node).data("ioid", ioid++);

			io.observe(node);
		});
	};

	pmw.startIntersectionObserverToWatch = () => {

		try {
			// enable view_item_list test mode from browser
			if (pmw.urlHasParameter("vildemomode")) pmwDataLayer.shop.view_item_list_trigger.test_mode = true;

			// set up intersection observer
			// https://stackoverflow.com/questions/62084306/intersectionobserver-not-working-in-safari-or-ios
			io = new IntersectionObserver(pmw.observerCallback, {
				root: null,
				// root     : document.body,
				threshold: pmwDataLayer.shop.view_item_list_trigger.threshold,
			});

			observeProductElements();
		} catch (e) {
			console.error(e);
		}
	};

	/**
	 * Pick up products that appeared after the initial observer run, whether
	 * they were lazy loaded, added by a page builder, or rendered by a block
	 * running on the Interactivity API.
	 *
	 * Called from the DOM refresh pipeline in interactivity-dom.js.
	 */
	pmw.observeNewProductElements = () => {

		try {
			observeProductElements();
		} catch (e) {
			console.error(e);
		}
	};

	// Watch DOM for new lazy loaded products and add them to the intersection observer
	pmw.startProductsMutationObserverToWatch = () => {

		try {
			// Pass in the target node, as well as the observer options

			// selects the most common parent node
			// https://stackoverflow.com/a/7648323/4688612
			let productsNode = jQuery(".pmwProductId:eq(0)").parents().has(jQuery(".pmwProductId:eq(1)").parents()).first();

			if (productsNode.length) {
				productsMutationObserver.observe(productsNode[0], {
					attributes   : true,
					childList    : true,
					characterData: true,
				});
			}
		} catch (e) {
			console.error(e);
		}
	};

	// Create an observer instance
	const productsMutationObserver = new MutationObserver(mutations => {

		mutations.forEach(mutation => {
			let newNodes = mutation.addedNodes; // DOM NodeList
			if (newNodes !== null) { // If there are new nodes added
				let nodes = jQuery(newNodes); // jQuery set
				nodes.each((i, node) => {
					if (
						// jQuery(this).hasClass("type-product") ||
						// jQuery(this).hasClass("product-small") ||
						// jQuery(this).hasClass("wc-block-grid__product")
						// jQuery(this).hasClass("type-product")
						isNestedClass(node) || isFlatClass(node)
					) {
						// check if the node has a child or sibling wpmProductId
						// if yes add it to the intersectionObserver
						if (hasPmwProductIdElement(node) && !jQuery(node).data("pmwObserved")) {
							jQuery(node).data("pmwObserved", true);
							jQuery(node).data("ioid", ioid++);
							io.observe(node);
						}
					}
				});
			}
		});
	});

	let hasPmwProductIdElement = elem =>
		!!(jQuery(elem).find(".pmwProductId").length ||
			jQuery(elem).siblings(".pmwProductId").length);

	pmw.getProductIdByCartItemElement = element => {

		const href = jQuery(element).find(".product-remove").find("a").attr("href");

		if (href) {
			const productIdFromKey = pmw.getProductIdByCartItemKeyUrl(new URL(href, window.location.href));
			if (productIdFromKey) return productIdFromKey;
		}

		const productId = jQuery(element).find("[data-product_id]").first().attr("data-product_id");
		return productId ? productId : null;
	};

	pmw.getProductQuantityByCartItemElement = element => {

		return jQuery(element).find(".qty").val() || null;
	};

	/**
	 * Read the tracked product ID out of a cart item key map entry.
	 *
	 * The map has two producers that disagreed on the entry shape: the inline
	 * script printed next to every cart item writes product_id / variation_id,
	 * while the pmw_get_cart_items response wrote id / is_variation / parent_id.
	 * Since the cart sync merges the response over the inline entries, a synced
	 * cart left the reader with neither field, and remove_from_cart stopped
	 * firing with "Wasn't able to retrieve a productId". The backend emits both
	 * shapes now; this reader accepts either, so an entry printed by an older
	 * cached fragment or by a third party still resolves. @since 1.67.1
	 *
	 * @param {object} entry  A pmwDataLayer.cart_item_keys entry.
	 * @returns {string|number|null} The variation ID for a variation, the
	 *                               product ID otherwise, null if unreadable.
	 */
	pmw.getProductIdByCartItemKeyEntry = entry => {

		if (!entry) return null;

		if (entry["variation_id"]) return entry["variation_id"];
		if (entry["product_id"]) return entry["product_id"];

		// The legacy pmw_get_cart_items shape: id is the variation for a
		// variation cart item and the product for everything else, which is
		// exactly what the two fields above resolve to.
		if (entry["id"]) return entry["id"];

		return null;
	};

	pmw.getProductIdByCartItemKeyUrl = url => {

		let searchParams = new URLSearchParams(url.search);
		let cartItemKey  = searchParams.get("remove_item");

		if (!cartItemKey) return null;

		return pmw.getProductIdByCartItemKeyEntry(pmwDataLayer?.cart_item_keys?.[cartItemKey]);
	};

	pmw.getAddToCartLinkProductIds = () =>
		jQuery("a").map(function () {
			let href = jQuery(this).attr("href");

			if (href && href.includes("?add-to-cart=")) {
				let matches = href.match(/(add-to-cart=)(\d+)/);
				if (matches) return matches[2];
			}
		}).get();

	pmw.doesWooCommerceCartExist = () => document.cookie.includes("woocommerce_items_in_cart");

	pmw.isWooCommerceActive = () => {
		return !!pmwDataLayer.shop;
	};

	pmw.isInWooCommerceSesssion = () => {

		if (pmwDataLayer?.shop?.session_active) return true;

		let regex           = new RegExp("woocommerce_cart_hash");
		let wcSessionCookie = pmw.getCookieThatContainsRegex(regex);

		if (wcSessionCookie) return true;

		return false;
	};

	pmw.isNotInWooCommerceSesssion = () => {
		return !pmw.isInWooCommerceSesssion();
	};

	pmw.registerShowVariationEventListener = () => {
		let previousVariationId = null;

		jQuery(".single_variation_wrap").on("show_variation", (event, variation) => {

			// Abort if lazy loading is enabled
			// if (pmwDataLayer?.general?.lazy_load_pmw) return;

			try {
				// If variations_output is disabled, don't fire view_item events from show_variation
				// because the parent product view_item is already triggered on page load
				if (!pmwDataLayer?.shop?.variations_output) return;

				let productId = pmw.getIdBasedOndVariationsOutputSetting(variation.variation_id);

				if (!productId) throw Error("Wasn't able to retrieve a productId");

				// If the variation id is not the same as the previous one, run the function
				if (previousVariationId !== variation.variation_id) {
					pmw.triggerViewItemEventPrep(productId);
					previousVariationId = variation.variation_id;
				}

			} catch (e) {
				console.error(e);
			}
		});
	};

}(window.pmw = window.pmw || {}, jQuery));
